<script setup>
import * as Sentry from '@sentry/vue'
import { computed, ref, watch } from 'vue'
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router'
import { clearSession, getToken, useAuth } from './auth.js'
import {
  setAutomaticTheme,
  setTheme,
  useTheme,
} from './theme.js'

const route = useRoute()
const router = useRouter()
const { isAdmin } = useAuth()
const { automatic, theme } = useTheme()
const showAppNav = computed(() => route.meta.requiresAuth)
const isMenuOpen = ref(false)
const dumpError = ref('')
const isDownloadingDump = ref(false)

watch(() => route.fullPath, () => {
  isMenuOpen.value = false
})

async function logout() {
  clearSession()
  await router.push({ name: 'login' })
}

function dumpFilename(contentDisposition) {
  const fallback = 'budget-database-dump.zip'
  if (!contentDisposition) {
    return fallback
  }

  const match = contentDisposition.match(/filename="([^"]+)"/)
  return match?.[1] || fallback
}

function logDatabaseDump(level, outcome, startedAt, attributes = {}) {
  Sentry.logger[level]('Database dump completed', {
    operation: 'database_dump',
    outcome,
    duration_ms: Math.round(performance.now() - startedAt),
    ...attributes,
  })
}

async function downloadDatabaseDump() {
  const startedAt = performance.now()
  dumpError.value = ''

  const token = getToken()
  if (!token) {
    logDatabaseDump('info', 'unauthorized', startedAt)
    clearSession()
    await router.push({ name: 'login' })
    return
  }

  isDownloadingDump.value = true
  try {
    const response = await fetch('/api/admin/database-dump', {
      headers: { Authorization: `Bearer ${token}` },
    })

    if (response.status === 401) {
      logDatabaseDump('info', 'unauthorized', startedAt, { http_status: response.status })
      clearSession()
      await router.push({ name: 'login' })
      return
    }

    if (!response.ok) {
      dumpError.value = 'Не удалось выгрузить базу данных'
      logDatabaseDump('warn', 'failed', startedAt, { http_status: response.status })
      return
    }

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = dumpFilename(response.headers.get('Content-Disposition'))
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
    logDatabaseDump('info', 'success', startedAt, { http_status: response.status })
  } catch (error) {
    dumpError.value = 'Не удалось выгрузить базу данных'
    Sentry.captureException(error, {
      tags: { operation: 'database_dump' },
      extra: {
        outcome: 'failed',
        duration_ms: Math.round(performance.now() - startedAt),
      },
    })
  } finally {
    isDownloadingDump.value = false
  }
}
</script>

<template>
  <header
    v-if="showAppNav"
    class="app-header"
  >
    <div class="app-header-top">
      <div>
        <h1 id="app-title">
          Личный бюджет
        </h1>
        <p
          v-if="!isAdmin"
          class="readonly-badge"
        >
          Только просмотр
        </p>
      </div>

      <div class="header-actions">
        <button
          type="button"
          class="menu-toggle"
          :aria-label="isMenuOpen ? 'Закрыть меню' : 'Открыть меню'"
          aria-controls="top-menu"
          :aria-expanded="isMenuOpen"
          @click="isMenuOpen = !isMenuOpen"
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </button>

        <div
          class="theme-selector"
          role="group"
          aria-label="Тема оформления"
        >
          <button
            type="button"
            class="theme-choice"
            :class="{ 'theme-choice--active': automatic }"
            :aria-pressed="automatic"
            aria-label="Авто (как в системе)"
            title="Авто (как в системе)"
            @click="setAutomaticTheme"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              class="action-icon"
            >
              <rect
                x="4"
                y="5"
                width="16"
                height="11"
                rx="2"
              />
              <path d="M8.5 19h7M12 16v3" />
            </svg>
          </button>
          <button
            type="button"
            class="theme-choice"
            :class="{ 'theme-choice--active': !automatic && theme === 'light' }"
            :aria-pressed="!automatic && theme === 'light'"
            aria-label="Светлая тема"
            title="Светлая тема"
            @click="setTheme('light')"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              class="action-icon"
            >
              <circle
                cx="12"
                cy="12"
                r="3.5"
              />
              <path d="M12 4v2.2M12 17.8v2.2M6.34 6.34l1.56 1.56M16.1 16.1l1.56 1.56M4 12h2.2M17.8 12h2.2M6.34 17.66l1.56-1.56M16.1 7.9l1.56-1.56" />
            </svg>
          </button>
          <button
            type="button"
            class="theme-choice"
            :class="{ 'theme-choice--active': !automatic && theme === 'dark' }"
            :aria-pressed="!automatic && theme === 'dark'"
            aria-label="Тёмная тема"
            title="Тёмная тема"
            @click="setTheme('dark')"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              class="action-icon"
            >
              <path d="M20 12.7A8 8 0 1 1 11.3 4 6.22 6.22 0 0 0 20 12.7Z" />
            </svg>
          </button>
        </div>

        <button
          v-if="isAdmin"
          type="button"
          class="icon-action dump-download"
          aria-label="Скачать dump базы данных"
          :disabled="isDownloadingDump"
          @click="downloadDatabaseDump"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            class="action-icon"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <path d="M7 10l5 5 5-5" />
            <path d="M12 15V3" />
          </svg>
        </button>

        <button
          type="button"
          class="icon-action logout"
          aria-label="Выйти"
          @click="logout"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            class="action-icon"
          >
            <path d="M10 5H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h4" />
            <path d="M15 7l5 5-5 5" />
            <path d="M20 12H9" />
          </svg>
        </button>
      </div>
    </div>

    <nav
      id="top-menu"
      class="top-menu"
      :class="{ 'top-menu--open': isMenuOpen }"
      aria-label="Основная навигация"
    >
      <RouterLink to="/">
        Отчеты
      </RouterLink>
      <RouterLink to="/budget">
        Бюджет
      </RouterLink>
      <RouterLink to="/debts">
        Долги
      </RouterLink>
      <RouterLink to="/accounts">
        Список счетов
      </RouterLink>
      <RouterLink
        v-if="isAdmin"
        to="/income"
      >
        Доход
      </RouterLink>
      <RouterLink
        v-if="isAdmin"
        to="/wishlist"
      >
        Список желаний
      </RouterLink>
    </nav>

    <p
      v-if="dumpError"
      class="header-error"
      role="status"
    >
      {{ dumpError }}
    </p>
  </header>

  <RouterView />
</template>

<style scoped>
.app-header {
  display: grid;
  grid-template-areas: "brand nav actions";
  grid-template-columns: minmax(0, 1fr) max-content minmax(0, 1fr);
  align-items: center;
  gap: var(--space-md);
  width: min(1280px, calc(100% - 2rem));
  margin: 0 auto;
  padding-top: var(--space-xl);
}

.app-header-top {
  display: contents;
}

.app-header-top > div:first-child {
  grid-area: brand;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
}

.header-actions {
  grid-area: actions;
  display: inline-flex;
  align-items: center;
  justify-self: center;
  gap: var(--space-sm);
}

.app-header h1 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.5rem;
  letter-spacing: -0.03em;
}

.readonly-badge {
  margin: var(--space-xs) 0 0;
  color: var(--warning-text);
  font-size: 0.75rem;
  font-weight: 600;
}

.top-menu {
  grid-area: nav;
  display: inline-flex;
  flex-shrink: 0;
  justify-self: center;
  gap: var(--space-sm);
  padding: 0.3rem;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-full);
  background: var(--surface-raised);
}

.top-menu a {
  padding: 0.45rem 0.85rem;
  border-radius: var(--radius-full);
  color: var(--text-secondary);
  font-weight: 700;
  text-decoration: none;
  text-wrap: nowrap;
  transition: background var(--duration-fast) var(--ease-out), color var(--duration-fast) var(--ease-out);
  white-space: nowrap;
}

.top-menu a:hover:not(.router-link-active) {
  background: var(--surface-sunken);
  color: var(--text-primary);
}

.top-menu a.router-link-active {
  background: var(--accent-bg);
  color: var(--accent-text);
}

.icon-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--touch-target-min);
  height: var(--touch-target-min);
  padding: 0;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-full);
  background: var(--surface-overlay);
  color: var(--text-primary);
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  touch-action: manipulation;
  transition: background var(--duration-fast) var(--ease-out);
}

.icon-action:disabled {
  background: var(--surface-sunken);
  color: var(--text-tertiary);
  cursor: wait;
}

.action-icon {
  width: 1.15rem;
  height: 1.15rem;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2;
}

.icon-action:hover:not(:disabled) {
  background: var(--surface-sunken);
}

.icon-action:focus-visible,
.menu-toggle:focus-visible,
.top-menu a:focus-visible {
  outline: 2px solid var(--border-focus);
  outline-offset: 2px;
}

.theme-selector {
  display: inline-flex;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-full);
  background: var(--surface-overlay);
}

.theme-choice {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--touch-target-min);
  height: var(--touch-target-min);
  min-height: var(--touch-target-min);
  padding: 0;
  border: 0;
  border-right: 1px solid var(--border-default);
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  touch-action: manipulation;
  transition: background var(--duration-fast) var(--ease-out), color var(--duration-fast) var(--ease-out);
}

.theme-choice:first-child {
  border-radius: var(--radius-full) 0 0 var(--radius-full);
}

.theme-choice:last-child {
  border-right: 0;
  border-radius: 0 var(--radius-full) var(--radius-full) 0;
}

.theme-choice:hover:not(.theme-choice--active) {
  background: var(--surface-sunken);
  color: var(--text-primary);
}

.theme-choice--active {
  background: var(--accent-bg);
  color: var(--accent-text);
}

/* Dividers never touch the accent fill, so both sides of the active
   segment are treated the same. */
.theme-choice--active,
.theme-choice:has(+ .theme-choice--active) {
  border-right-color: transparent;
}

.theme-choice:focus-visible {
  position: relative;
  z-index: var(--z-raised);
  outline: 2px solid var(--border-focus);
  outline-offset: -2px;
}

.theme-choice--active:focus-visible {
  outline-color: var(--accent-text);
  outline-offset: -4px;
}

.header-error {
  grid-column: 1 / -1;
  justify-self: end;
  margin: 0;
  color: var(--error-text);
  font-size: 0.85rem;
  font-weight: 600;
  text-align: right;
}

.menu-toggle {
  display: none;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: var(--space-xs);
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-full);
  background: var(--surface-overlay);
  color: var(--text-primary);
  cursor: pointer;
  touch-action: manipulation;
  transition: background var(--duration-fast) var(--ease-out);
}

.menu-toggle span {
  display: block;
  width: 1.1rem;
  height: 2px;
  border-radius: var(--radius-full);
  background: currentColor;
}

.menu-toggle:hover {
  background: var(--surface-sunken);
}

@media (max-width: 1280px) {
  .app-header {
    display: flex;
    align-items: stretch;
    flex-direction: column;
    padding-top: var(--space-md);
  }

  .app-header-top {
    display: grid;
    grid-template-areas:
      "brand"
      "actions";
    grid-template-columns: 1fr;
    justify-items: center;
    gap: var(--space-md);
  }

  .header-actions {
    flex-shrink: 0;
    flex-wrap: nowrap;
    justify-content: center;
    gap: var(--space-xs);
  }

  .menu-toggle {
    display: inline-flex;
    width: var(--touch-target-min);
    height: var(--touch-target-min);
  }

  .header-error {
    text-align: left;
  }

  .top-menu {
    display: none;
    width: 100%;
    overflow-x: visible;
    flex-direction: column;
    align-items: stretch;
    border-radius: var(--radius-xl);
    padding: 0.45rem;
  }

  .top-menu--open {
    display: flex;
  }

  .top-menu a {
    flex: 1;
    min-height: var(--touch-target-min);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    white-space: nowrap;
  }
}
</style>
